import { isRouteErrorResponse, Link, useLocation, useRouteError } from 'react-router';

import { Page } from '../shell/Page.tsx';
import styles from './ErrorPage.module.css';

// Design: NOT FOUND / SOMETHING WENT WRONG. A code tile, a plain title, what
// to do next, and a reference to quote to support.
function ErrorScreen({ code, title, text, primary, secondary, reference }: {
  code: string;
  title: string;
  text: string;
  primary: { label: string; to?: string; onClick?: () => void };
  secondary: { label: string; to?: string; href?: string };
  reference: string;
}) {
  return (
    <div className={styles.screen}>
      <span className={styles.code} aria-hidden="true">{code}</span>
      <h2 className={styles.title}>{title}</h2>
      <p className={styles.text}>{text}</p>
      {primary.to ? <Link to={primary.to} className={styles.primary}>{primary.label}</Link> : <button type="button" className={styles.primary} onClick={primary.onClick}>{primary.label}</button>}
      {secondary.to ? <Link to={secondary.to} className={styles.secondary}>{secondary.label}</Link> : <a href={secondary.href} className={styles.secondary}>{secondary.label}</a>}
      <p className={styles.reference}>{reference}</p>
    </div>
  );
}

export function ErrorPage() {
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  // A new version was deployed while this tab was open: reload once.
  const staleChunk = error instanceof TypeError && /dynamically imported module|Importing a module script/i.test(error.message);
  if (notFound) return <NotFoundPage />;
  return (
    <div className={styles.outside}>
      <Page title="Something went wrong" backTo="/">
        <ErrorScreen
          code="!"
          title={staleChunk ? 'Guildite was updated' : 'Something went wrong on our side'}
          text={staleChunk ? 'A new version came out while this page was open. Reload to get it.' : 'It’s not you. Nothing was charged. Try again in a moment. If it keeps happening, tell us and we’ll look into it.'}
          primary={{ label: staleChunk ? 'Reload' : 'Try again', onClick: () => window.location.reload() }}
          secondary={{ label: 'Report this problem', href: '/help?topic=other' }}
          reference={`Error on ${window.location.pathname}`}
        />
      </Page>
    </div>
  );
}

export function NotFoundPage() {
  const location = useLocation();
  return (
    <Page title="Not found" backTo="/">
      <ErrorScreen
        code="404"
        title="This page isn’t here"
        text="The listing may have been sold or removed, or the link is wrong. Nothing is broken on your side."
        primary={{ label: 'Go to Home', to: '/' }}
        secondary={{ label: 'Search Guildite', to: '/discover' }}
        reference={`guildite.com${location.pathname}`}
      />
    </Page>
  );
}
